import React, { useState, useEffect } from 'react';
import { BarChart2, Link2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const PLATFORMS = [
  { key: 'google_ads', label: 'Google Ads', color: '#4285F4' },
  { key: 'meta_ads', label: 'Meta Ads', color: '#0866FF' },
  { key: 'linkedin_ads', label: 'LinkedIn Ads', color: '#0A66C2' },
  { key: 'google_analytics', label: 'Google Analytics', color: '#E37400' },
];

export default function ReportsPage() {
  const { api } = useAuth();
  const [reports, setReports] = useState(null);
  const [integrations, setIntegrations] = useState({});
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ platform: '', api_key: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('get', '/api/portal/reports'),
      api('get', '/api/portal/integrations'),
    ]).then(([r, i]) => {
      setReports(r.data);
      setIntegrations(i.data?.connected || {});
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api('post', '/api/portal/integrations/manual', connectForm);
      toast.success(`${connectForm.platform} gekoppeld!`);
      setIntegrations(i => ({ ...i, [connectForm.platform]: true }));
      setShowConnect(false);
      window.location.reload();
    } catch { toast.error('Koppelen mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#D4A017] border-t-transparent rounded-full animate-spin" /></div>;

  const hasIntegrations = Object.values(integrations).some(Boolean);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Rapportages</h1>
          <p className="text-[#64748b] text-sm mt-1">Overzicht van uw marketingprestaties</p>
        </div>
        <button onClick={() => setShowConnect(!showConnect)} className="flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-4 py-2 rounded-xl text-sm transition-all">
          <Link2 size={16} /> Account koppelen
        </button>
      </div>

      {/* Connect modal */}
      {showConnect && (
        <div className="bg-[#0f172a] rounded-2xl border border-[#D4A017]/30 p-6 mb-6">
          <h3 className="text-white font-bold mb-4">Advertentieaccount koppelen</h3>
          <form onSubmit={handleConnect} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Platform</label>
              <select value={connectForm.platform} onChange={e => setConnectForm(f => ({ ...f, platform: e.target.value }))} required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#D4A017]">
                <option value="">Selecteer platform</option>
                {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#94a3b8] mb-2">Account ID / API Key</label>
              <input value={connectForm.api_key} onChange={e => setConnectForm(f => ({ ...f, api_key: e.target.value }))} required
                placeholder="Voer uw account ID of API key in"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017]" />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="bg-[#D4A017] text-[#0f172a] font-bold px-5 py-2 rounded-lg text-sm disabled:opacity-60">
                {saving ? 'Koppelen...' : 'Koppelen'}
              </button>
              <button type="button" onClick={() => setShowConnect(false)} className="bg-white/10 text-white px-5 py-2 rounded-lg text-sm">
                Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Platform status */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {PLATFORMS.map(p => (
          <div key={p.key} className={`bg-[#0f172a] rounded-xl border p-4 ${integrations[p.key] ? 'border-green-500/30' : 'border-white/5'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium text-sm">{p.label}</span>
              <span className={`w-2 h-2 rounded-full ${integrations[p.key] ? 'bg-green-400' : 'bg-[#475569]'}`} />
            </div>
            <p className={`text-xs ${integrations[p.key] ? 'text-green-400' : 'text-[#475569]'}`}>
              {integrations[p.key] ? 'Gekoppeld' : 'Niet gekoppeld'}
            </p>
          </div>
        ))}
      </div>

      {/* Reports or empty state */}
      {!hasIntegrations ? (
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-[#475569] mx-auto mb-4" />
          <h3 className="text-white font-bold text-lg mb-2">Geen accounts gekoppeld</h3>
          <p className="text-[#64748b] mb-6 max-w-md mx-auto">Koppel uw advertentieaccounts om uw marketingprestaties te bekijken. Klik op "Account koppelen" hierboven.</p>
          <button onClick={() => setShowConnect(true)} className="bg-[#D4A017] text-[#0f172a] font-bold px-6 py-3 rounded-xl hover:bg-[#B8860B] transition-all">
            Account koppelen
          </button>
        </div>
      ) : (
        <div className="bg-[#0f172a] rounded-2xl border border-white/5 p-8 text-center">
          <BarChart2 className="w-12 h-12 text-[#D4A017] mx-auto mb-4" />
          <h3 className="text-white font-bold text-lg mb-2">Rapportages worden geladen</h3>
          <p className="text-[#64748b]">Uw data wordt gesynchroniseerd. Kom morgen terug voor de eerste rapportage.</p>
        </div>
      )}
    </div>
  );
}
